// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

// ************************************************************************
// IF YOU TOUCH THIS FILE MAKE SURE IT WORKS IN ALL AREAS THIS FORM IS USED.
// ************************************************************************

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppEvent } from '@interfaces/interfaces';
import { User, UserTypes } from '@interfaces/user.interfaces';
import { Observable, debounceTime, firstValueFrom, of, takeWhile, tap } from 'rxjs';
import { UserService } from '../../services/user.service';
import { emailPattern, namePattern, phoneExporter, phoneImporter, showFormErrors, usPhonePattern } from '../../utils/form-utils';
import { PhonePipe } from '../../pipes/phone.pipe';
import { formatPhone } from 'src/app/utils/format-util';

@Component({
    selector: 'profile-info-form-component',
    templateUrl: './profile-info-form.component.html',
    styleUrls: ['./profile-info-form.component.scss'],
})
export class ProfileInfoFormComponent implements OnChanges, OnDestroy, OnInit {
    @Input() existingUser: User;
    @Input() allowExistingUser = false;
    @Input() disableForm = false;
    @Input() profileFormProperties: any;
    @Input() userFound = false;
    // Ex: Editing a buyer's information on a deal when a co-buyer is attached
    @Input() offLimitsUserIds: number[] = [];
    @Output() outEvent = new EventEmitter<AppEvent>();
    @ViewChild('userPhone') userPhone;

    private isComponentAlive = true;

    public profileInfoForm: FormGroup<{
        firstName: FormControl<string>;
        middleName: FormControl<null>;
        lastName: FormControl<string>;
        email: FormControl<string>;
        phone: FormControl<string>;
    }>;
    public originalUser: User;
    public checkingUser = false;

    get originalEmail(): boolean {
        return this.originalUser?.email === this.existingUser?.email;
    }

    get activeUserType(): UserTypes {
        return this.userService.user?.userType;
    }

    get existingUserIsBuyer(): boolean {
        return this.existingUser?.userType === UserTypes.Customer;
    }

    get existingUserIsLoggedIn(): boolean {
        return this.existingUser?.email === this.userService.user?.email;
    }

    get userOffLimits(): boolean {
        // An array of off limits userIds were passed in and the existingUser found is inside that array.
        return this.existingUser && this.offLimitsUserIds.includes(this.existingUser?.userId);
    }

    get disableSave(): boolean {
        // Disable saving if we are checking for a user
        // Or if we found a user, but we either don't allow existing accounts, it was marked as off limits, or the account is not a buyer
        return this.checkingUser || (this.userFound && !this.originalEmail && (!this.allowExistingUser || this.userOffLimits || !this.existingUserIsBuyer));
    }

    constructor(
        private fb: FormBuilder,
        private userService: UserService,
        private phonePipe: PhonePipe
    ) {
        this.buildForm();
    }

    ngOnInit(): void {
        this.setFormValues(this.profileFormProperties);
        this.originalUser = this.existingUser;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.disableForm) {
            this.profileInfoForm.disable();
        }

        if (changes.profileFormProperties?.currentValue) {
            this.setFormValues(changes.profileFormProperties?.currentValue);
        } else {
            this.setFormValues(this.profileFormProperties);
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    closeModal(success: any = false): void {
        this.outEvent.emit(success);
    }

    formChanges(): void {
        this.profileInfoForm.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(300)
            )
            .subscribe((value) => {
                value.email.match(/\s+$/) && this.profileInfoForm.controls.email.setValue(value.email.trim());
                // Used to prevent any infinite loop from ocurring where findUser udpates the form and triggers the valueChanges
                if (this.profileInfoForm.controls.email.valid && value.email !== this.existingUser?.email) {
                    this.findUser(value.email).subscribe();
                }
            });
    }

    buildForm() {
        this.profileInfoForm = this.fb.group({
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            middleName: [null, Validators.compose([Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            phone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
        });

        this.formChanges();
    }

    findUser(email: string): Observable<User> {
        if (!this.profileFormProperties?.email || !this.profileInfoForm.controls['email'].valid) {
            return of(null);
        }

        this.checkingUser = true;

        return this.userService.checkUserByEmail$(email).pipe(
            takeWhile(() => this.isComponentAlive),
            tap((user) => {
                if (user?.userId) {
                    this.userFound = true;
                    this.existingUser = user;

                    // Don't want to propagate dealer or internal user info
                    // Don't propagate any info if the current user is a customer.
                    //  - Only case this should occur is if a customer is editing their own account in Buyer Settings
                    // Only propagate dealer/internal if the user was editing from dealer settings and originalUser was set to the account.
                    if ((user?.userType === UserTypes.Customer || this.originalEmail) && this.activeUserType !== UserTypes.Customer) {
                        this.profileFormProperties = user;
                        this.setFormValues(this.profileFormProperties);
                    }
                } else {
                    this.userFound = false;
                    this.existingUser = undefined;
                }

                this.checkingUser = false;
            })
        );
    }

    public async save() {
        // Make sure we do not let an existing user through if we do not allow it.
        // Also verify the existing user is not the same as the original if we stop them.
        if (this.existingUser && !this.originalEmail && (!this.allowExistingUser || this.activeUserType === UserTypes.Customer)) {
            return;
        }

        if (this.existingUser && !this.existingUserIsBuyer && !this.originalEmail) {
            return;
        }
        if (this.profileInfoForm.status === 'INVALID') {
            showFormErrors(this.profileInfoForm.controls);
            return;
        }

        const userEmail = this.existingUser?.email;
        const formEmail = this.profileInfoForm.controls.email.value;

        let currentUser = this.existingUser;
        if (userEmail !== formEmail) {
            // the email has been updated, and we don't have the new user's info available yet
            currentUser = await firstValueFrom(this.findUser(formEmail));
        }

        if (!currentUser) {
            // this is a new, unknown, email address, and we're using the original buyer as a template for this new user
            currentUser = this.originalUser;
        }

        const phone = phoneExporter(this.profileInfoForm.controls.phone.value);

        // patch the user with the updated values
        currentUser.email = this.profileInfoForm.controls.email.value;
        currentUser.firstName = this.profileInfoForm.controls.firstName.value;
        currentUser.middleName = this.profileInfoForm.controls.middleName.value;
        currentUser.lastName = this.profileInfoForm.controls.lastName.value;
        currentUser.phone = phone;

        this.closeModal(currentUser);
    }

    private setFormValues(data: any): void {
        this.existingUser = data;

        const phone = data.phone.match(usPhonePattern) ? data.phone : phoneImporter(data.phone);
        this.profileInfoForm.setValue({
            firstName: data.firstName,
            middleName: data.middleName ?? '',
            lastName: data.lastName,
            email: data.email,
            phone: formatPhone(phone),
        });
    }
}
